import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CaretRight, Info, Warning } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '@/components/ui/accordion';
import { PageHeader } from '@/components/enterprise/PageHeader';
import { StatusBadge } from '@/components/enterprise/StatusBadge';
import { FAQ, GUIDE_LANG_KEY, SECTIONS, STAGES, UI } from './guideContent';
import { GUIDE_KB_SLUG } from './GuideHelpButton';
import { knowledgeApi } from '@/pages/knowledge/knowledgeApi';

const MAINTAINER_MAIL = 'admin@sensbey.co.jp';

const jump = (id) => {
  const el = document.getElementById(`guide-${id}`);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const Callout = ({ tone, text }) => {
  const warn = tone === 'warn';
  const Icon = warn ? Warning : Info;
  return (
    <div data-testid={`guide-callout-${tone}`}
      className={`flex gap-2.5 rounded-lg border px-3.5 py-3 ${
        warn ? 'border-amber-300 bg-amber-50' : 'border-teal-200 bg-teal-50'}`}>
      <Icon size={17} weight="duotone"
        className={`mt-0.5 shrink-0 ${warn ? 'text-amber-700' : 'text-teal-700'}`} />
      <p className={`text-sm leading-relaxed ${warn ? 'text-amber-900' : 'text-teal-900'}`}>{text}</p>
    </div>
  );
};

const Block = ({ b, lang, t }) => {
  if (b.t === 'p') return <p className="text-sm leading-relaxed text-zinc-700">{b[lang]}</p>;
  if (b.t === 'sub') {
    return <h3 className="pt-1 text-base font-semibold text-zinc-900">{b[lang]}</h3>;
  }
  if (b.t === 'callout') return <Callout tone={b.tone} text={b[lang]} />;
  if (b.t === 'list') {
    return (
      <ul className="space-y-2">
        {b[lang].map((x) => (
          <li key={x} className="flex gap-2 text-sm leading-relaxed text-zinc-700">
            <CaretRight size={13} className="mt-1 shrink-0 text-teal-700" />
            <span>{x}</span>
          </li>
        ))}
      </ul>
    );
  }
  if (b.t === 'fields') {
    return (
      <div className="rounded-lg border border-zinc-200 bg-zinc-50/70 px-3.5 py-3">
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-zinc-500">{t.fields}</p>
        <div className="flex flex-wrap gap-1.5">
          {b[lang].map((f) => (
            <span key={f}
              className="whitespace-nowrap rounded-md border border-zinc-300 bg-white px-2 py-0.5 text-xs font-medium text-zinc-700">
              {f}
            </span>
          ))}
        </div>
      </div>
    );
  }
  if (b.t === 'table') {
    const head = lang === 'ja' ? b.headJa : b.headEn;
    const rows = lang === 'ja' ? b.rowsJa : b.rowsEn;
    return (
      <div className="overflow-x-auto rounded-lg border border-zinc-200">
        <table className="w-full min-w-[520px] text-sm" data-testid="guide-gate-table">
          <thead className="bg-zinc-100 text-left text-zinc-700">
            <tr>{head.map((h) => (
              <th key={h} className="whitespace-nowrap px-3 py-2 font-semibold">{h}</th>))}
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-200 bg-white">
            {rows.map(([cond, effect, next]) => {
              const [tone, label] = effect.split(':');
              return (
                <tr key={cond}>
                  <td className="px-3 py-2 font-medium text-zinc-900">{cond}</td>
                  <td className="px-3 py-2">
                    <StatusBadge tone={tone === 'blocks' ? 'danger' : 'neutral'}>{label}</StatusBadge>
                  </td>
                  <td className="px-3 py-2 text-zinc-700">{next}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }
  return null;
};

export default function DevGuide() {
  const [lang, setLang] = useState(() => localStorage.getItem(GUIDE_LANG_KEY) || 'ja');
  useEffect(() => { localStorage.setItem(GUIDE_LANG_KEY, lang); }, [lang]);
  const t = useMemo(() => UI[lang], [lang]);
  // ナレッジ記事のコメント欄は編集画面にしか描画されないため、KB編集権限がある人
  // だけをコメントスレッドへ案内し、それ以外はメールにする（新しい仕組みは作らない）。
  const [kbEdit, setKbEdit] = useState(false);
  useEffect(() => {
    knowledgeApi.meta().then((m) => setKbEdit(!!m.can_edit)).catch(() => setKbEdit(false));
  }, []);

  return (
    <div className="space-y-6 pb-16" data-testid="dev-guide-page">
      <PageHeader
        eyebrow={<span className="text-xs font-semibold text-teal-700">{t.eyebrow}</span>}
        title={t.title} description={t.lead} testid="dev-guide-header"
        primary={
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 rounded-lg border border-zinc-300 bg-white p-0.5"
              role="group" aria-label={t.langLabel}>
              {['ja', 'en'].map((k) => (
                <button key={k} type="button" onClick={() => setLang(k)}
                  data-testid={`dev-guide-lang-${k}`}
                  className={`rounded-md px-2.5 py-1 text-xs font-semibold transition-colors ${
                    lang === k ? 'bg-teal-700 text-white' : 'text-zinc-600 hover:text-zinc-900'}`}>
                  {k === 'ja' ? '日本語' : 'English'}
                </button>
              ))}
            </div>
            <Button asChild variant="outline" data-testid="dev-guide-back">
              <Link to="/engineering/developments">
                <ArrowLeft size={15} className="mr-1" /> {t.back}
              </Link>
            </Button>
          </div>
        } />

      <Card data-testid="dev-guide-lifecycle">
        <CardContent className="p-4">
          <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-zinc-500">{t.lifecycle}</p>
          <div className="flex flex-wrap items-stretch gap-2">
            {STAGES.map((s, i) => (
              <div key={s.no} className="flex items-stretch gap-2">
                <button type="button" onClick={() => jump(s.section)}
                  data-testid={`dev-guide-stage-${s.no}`}
                  className="min-w-[150px] flex-1 rounded-lg border border-zinc-200 bg-white px-3 py-2 text-left transition-colors hover:border-teal-400 hover:bg-teal-50">
                  <span className="block font-mono text-[10px] text-zinc-400">{s.no}</span>
                  <span className="block text-sm font-semibold text-zinc-900">{lang === 'ja' ? s.ja : s.en}</span>
                  <span className="mt-0.5 block text-xs leading-snug text-zinc-600">
                    {lang === 'ja' ? s.descJa : s.descEn}
                  </span>
                </button>
                {i < STAGES.length - 1 && (
                  <CaretRight size={13} className="my-auto shrink-0 text-zinc-300" />
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-col gap-6 lg:flex-row">
        <nav className="lg:sticky lg:top-4 lg:h-fit lg:w-56 lg:shrink-0" data-testid="dev-guide-toc">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-zinc-500">{t.toc}</p>
          <ul className="space-y-0.5">
            {SECTIONS.map((s) => (
              <li key={s.id}>
                <button type="button" onClick={() => jump(s.id)}
                  data-testid={`dev-guide-toc-${s.id}`}
                  className="w-full rounded-md px-2 py-1.5 text-left text-sm text-zinc-700 transition-colors hover:bg-zinc-200/70 hover:text-zinc-900">
                  {lang === 'ja' ? s.navJa : s.navEn}
                </button>
              </li>
            ))}
            <li>
              <button type="button" onClick={() => jump('faq')} data-testid="dev-guide-toc-faq"
                className="w-full rounded-md px-2 py-1.5 text-left text-sm text-zinc-700 transition-colors hover:bg-zinc-200/70 hover:text-zinc-900">
                {t.faq}
              </button>
            </li>
          </ul>
        </nav>

        <div className="min-w-0 flex-1 space-y-6">
          {SECTIONS.map((s) => (
            <section key={s.id} id={`guide-${s.id}`} className="scroll-mt-4"
              data-testid={`dev-guide-section-${s.id}`}>
              <Card>
                <CardContent className="space-y-3 p-5">
                  <h2 className="text-lg font-bold text-zinc-900">{lang === 'ja' ? s.titleJa : s.titleEn}</h2>
                  {s.blocks.map((b, i) => <Block key={i} b={b} lang={lang} t={t} />)}
                </CardContent>
              </Card>
            </section>
          ))}

          <section id="guide-faq" className="scroll-mt-4" data-testid="dev-guide-section-faq">
            <Card>
              <CardContent className="p-5">
                <h2 className="text-lg font-bold text-zinc-900">{t.faq}</h2>
                <p className="mt-1 text-sm text-zinc-600">{t.faqHint}</p>
                <Accordion type="single" collapsible className="mt-3" data-testid="dev-guide-faq">
                  {FAQ.map((f) => (
                    <AccordionItem key={f.id} value={f.id}>
                      <AccordionTrigger data-testid={`dev-guide-faq-q-${f.id}`}
                        className="text-left text-sm font-semibold text-zinc-900">
                        {lang === 'ja' ? f.qJa : f.qEn}
                      </AccordionTrigger>
                      <AccordionContent data-testid={`dev-guide-faq-a-${f.id}`}>
                        <p className="text-sm leading-relaxed text-zinc-700">
                          {lang === 'ja' ? f.aJa : f.aEn}
                        </p>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          </section>

          <p className="px-1 text-xs text-zinc-500">{t.updated}</p>
          <p className="px-1">
            {kbEdit ? (
              <a href={`/knowledge/p/${GUIDE_KB_SLUG}/edit`} target="_blank" rel="noopener noreferrer"
                data-testid="dev-guide-feedback-comment"
                className="text-xs font-medium text-teal-700 hover:underline">
                {t.feedbackComment}
              </a>
            ) : (
              <a href={`mailto:${MAINTAINER_MAIL}?subject=${encodeURIComponent('開発案件の使い方ガイドについて / Development Project guide')}`}
                data-testid="dev-guide-feedback-mail"
                className="text-xs font-medium text-teal-700 hover:underline">
                {t.feedbackMail}
              </a>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
